// AggregateDeltaBanner — entity-agnostic Δ summary.
//
// Reads a changeset (`{field: {old, new}}`) and a config that names
// the financial fields + their headline order. Renders one number:
// the signed Δ of the highest-priority financial field that actually
// changed, plus a count of other touched fields.
import React from 'react';

function formatDelta(oldVal, newVal) {
  const o = Number(oldVal ?? 0);
  const n = Number(newVal ?? 0);
  if (!Number.isFinite(o) || !Number.isFinite(n)) return null;
  const delta = n - o;
  const abs = Math.abs(delta).toLocaleString('ar-SA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  if (delta === 0) return { sign: 'zero', text: `±${abs} SAR` };
  return {
    sign: delta > 0 ? 'plus' : 'minus',
    text: `${delta > 0 ? '+' : '−'}${abs} SAR`,
  };
}

export function buildAggregate(changeset, { financialFields, headlineFieldOrder }) {
  const keys = Object.keys(changeset || {});
  if (keys.length === 0) return null;
  const financial = keys.filter((k) => financialFields.has(k));
  const other = keys.length - financial.length;
  let headline = null;
  for (const k of headlineFieldOrder) {
    if (financial.includes(k)) { headline = k; break; }
  }
  if (!headline && financial.length > 0) headline = financial[0];
  const delta = headline
    ? formatDelta(changeset[headline].old, changeset[headline].new)
    : null;
  return { headline, delta, financialCount: financial.length, otherCount: other };
}

export default function AggregateDeltaBanner({ changeset, config, testIdPrefix = 'diff' }) {
  const agg = buildAggregate(changeset, config);
  if (!agg) return null;
  const { headline, delta, financialCount, otherCount } = agg;
  const tone =
    delta?.sign === 'plus'  ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
    delta?.sign === 'minus' ? 'bg-rose-50 border-rose-200 text-rose-800' :
    'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div
      className={`border rounded-lg p-3 mb-2 flex flex-wrap items-baseline gap-3 ${tone}`}
      data-testid={`${testIdPrefix}-aggregate-banner`}
    >
      <div className="text-xs font-medium uppercase tracking-wide opacity-60">صافي التغيير</div>
      {delta ? (
        <div className="text-xl font-bold font-mono" data-testid={`${testIdPrefix}-aggregate-delta`}>
          {delta.text}
          <span className="text-xs font-normal opacity-70 mr-2">({headline})</span>
        </div>
      ) : (
        <div className="text-sm opacity-70">لا توجد تغييرات مالية</div>
      )}
      <div className="text-xs opacity-70 ms-auto" data-testid={`${testIdPrefix}-aggregate-counts`}>
        {financialCount} حقل مالي
        {otherCount > 0 && ` · ${otherCount} حقل آخر`}
      </div>
    </div>
  );
}

export { formatDelta };
